import {HtmlPlayerComponent} from '../html-player/HtmlPlayer';
import PropTypes from 'prop-types';
import React from 'react';
import root from 'window-or-global';
import {connect} from 'react-redux';
import {mapMessageStateToProps} from '../../../message/Message';
import WithFullScreen from '../../../../hocs/with-fullscreen/WithFullScreen';
import VideoApi from '../../../../api/Video.api';
import {video} from '../../../../constants';
import RatingOverlay from '../../../rating-overlay/RatingOverlay';
import styles from './EpicPlayer.pcss';
import {getValueIn} from '../../../../../src/utils/ValueUtils';
import {isEmptyObject} from 'epic-node-cms/src/cmsUtils/utils';

/**
 * Epic player component
 *
 * @class EpicPlayerComponent
 * @extends HtmlPlayerComponent
 */
export class EpicPlayerComponent extends HtmlPlayerComponent {
    static propTypes = {
        ...HtmlPlayerComponent.propTypes,
        locale: PropTypes.string,
        sources: PropTypes.any,
        rating: PropTypes.arrayOf(
            PropTypes.shape({
                altText: PropTypes.string,
                src: PropTypes.string
            })
        ),
        epicStillType: PropTypes.string,
        captions: PropTypes.array
    };

    mediaServiceInfo = {};
    loaded = false;
    initialSeek = true;
    captions = [];
    isMounted = false;
    overlayTimeout = 0;
    overlayStartTime = 0;
    overlayTimeLeft = 0;
    specificMediaData = {
        manifest: {},
        captions: []
    };

    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            overlayVisible: false
        };

        this.videoApi = new VideoApi();
    }

    /**
     * Mounted tracking for async calls
     */
    componentWillUnmount() {
        this.isMounted = false;
    }

    componentDidMount() {
        super.componentDidMount();
        this.isMounted = true;
        const {epicStillType, epicStillDurationSeconds, rating} = this.props;

        if (rating && rating.length && epicStillType) {
            this.epicStillType = epicStillType.includes('OVERLAY')
                ? video.epicStillTypes.OVERLAY_INT
                : epicStillType;
            this.overlayTime = epicStillDurationSeconds;
        }
    }

    /**
     * GraphQL query for Media Service given a mediaRefId. Query returns media metadata.
     * Secondly, the data is traversed to find the manifest URL and returned.
     * @param mediaRefId - Media Reference ID for specific epicHosted 'recipe'.
     */
    async retrieveMediaData(mediaRefId) {
        console.debug('mediaRefId to get info on:', mediaRefId);
        this.mediaServiceInfo = await this.videoApi.getVideoManifest(mediaRefId);

        const dataRetrievedAndMounted = !this.mediaServiceInfo.errors && this.isMounted;

        if (!dataRetrievedAndMounted) {
            this.mediaDataFailure();
            return;
        }

        console.debug('MSI', this.mediaServiceInfo);

        const outputs =
            getValueIn(this.mediaServiceInfo, 'data', 'Media', 'getMediaRef', 'outputs') || [];

        await this.locateMetaData(outputs);
        this.hideWithoutManifest();
        this.renderCaptions();
    }

    /**
     * Error handler for Media Service mediaRefId metadata retrieval.
     */
    mediaDataFailure() {
        if (this.mediaServiceInfo.errors) {
            console.error(
                'Failed to get Media Service data for asset',
                this.mediaServiceInfo.errors
            );
            if (this.isMounted) this.setState({show: false});
        } else {
            console.debug('Attempting to get manifest/playlist data when not mounted');
        }
    }

    /**
     * If captions exist, render them and the UI components.
     */
    renderCaptions() {
        /**
         * If we received captions, render them
         */
        const isCaptions = this.specificMediaData.captions.length > 0;
        if (isCaptions) {
            if (this.isMounted) {
                this.setState({captionsAvailable: true});
                this.setCaptionMode(this.state.captionVisible ? 'showing' : 'hidden');
            }
        }
    }

    /**
     * If the Media Service query was successful but a suitable manifest was not found.
     */
    hideWithoutManifest() {
        if (isEmptyObject(this.specificMediaData.manifest)) {
            console.error('Failed to get manifest data for asset');
            if (this.isMounted) this.setState({show: false});
        }
    }

    async locateMetaData(outputs) {
        const isDefaultTrack = true;
        const that = this;
        outputs
            .filter(({key}) => key === 'manifest')
            .forEach(mediaData => {
                console.debug(`Loading: ${mediaData.url}`);
                that.specificMediaData.manifest = mediaData;
            });

        const mediaItems = Promise.all(
            outputs.map(data => this.captionBuilder(data, isDefaultTrack))
        );

        await mediaItems;
    }
    /**
     * Helper function to build the list of available captions.
     * @param mediaData - media metadata retrieved from Media Service
     * @param isDefaultTrack - to set this as teh default TextTrack.
     */
    captionBuilder = async (mediaData, isDefaultTrack) => {
        /**
         * Get captions/subtitles. The string comparisons here are contractual with the CMS.
         */
        const capCheck = mediaData.key.includes('captions');
        const subCheck = mediaData.key.includes('subtitles');
        if (subCheck || capCheck) {
            const lang = subCheck
                ? mediaData.key.replace('subtitles_', '')
                : mediaData.key.replace('captions_', '');
            const url = await blobifyDataFromUrl(mediaData.url, 'text/vtt');

            this.specificMediaData.captions.push({
                lang,
                def: isDefaultTrack,
                kind: subCheck ? 'subtitles' : 'captions',
                url
            });
        }
    };

    isStillType(stillType) {
        return this.epicStillType && this.epicStillType === stillType;
    }

    /**
     * EVENTS
     */

    /**
     * Click event handler that also evaluates for double clicks to toggle
     * fullscreen mode if enabled via CMS
     */
    onClick = () => {
        if (!this.props.hasFullScreen) return this.toggleVideo();

        clearTimeout(this.clickTimer);

        if (Date.now() - this.clickLast < this.clickDelay) {
            // Double click
            this.toggleFullScreen();
        } else {
            // Single click
            this.clickLast = Date.now();
            this.clickTimer = setTimeout(() => this.toggleVideo(), this.clickDelay);
        }
    };
    clickDelay = 200;
    clickLast = 0;
    clickTimer = null;

    /**
     * Listening to 'paused' event in part to ensure the 'small overlay' type
     * rating overlay is displayed for X seconds of playback and not just X seconds.
     */
    onPause() {
        super.onPause();
        if (this.isStillType(video.epicStillTypes.OVERLAY_INT)) {
            const now = Date.now();
            this.overlayTimeLeft = this.overlayTime - (now - this.overlayStartTime) / 1000;

            if (this.overlayTimeLeft < this.overlayTime) {
                console.debug('clearing overlay timeout', this.overlayTimeout);
                clearTimeout(this.overlayTimeout);
            }
        }
    }

    /**
     * Listening to 'playing' event in part to ensure the 'small overlay' type
     * rating overlay is displayed for X seconds of playback and not just X seconds.
     */
    onPlaying() {
        super.onPlaying();
        if (this.isStillType(video.epicStillTypes.OVERLAY_INT) && this.overlayTimeLeft > 0) {
            this.overlayStartTime = Date.now();
            console.debug('resetting overlay timeout');
            this.delayRemove(this.overlayTimeLeft * 1000);
        }
    }

    /**
     * Override to handle rating slate type APPEND
     */
    onEnded() {
        super.onEnded();
        if (this.isStillType(video.epicStillTypes.APPEND)) this.showOverlay(true);
    }

    /**
     * The user might seek back into the time 'this.overlayTime', so for PREPEND and OVERLAY types
     * we want to make sure we show the rating slate or overlay. In the APPEND case, we want to make sure
     * the slate is shown at the end in case of a seek, and remove it if they seek away from the end.
     */
    onSeeked() {
        super.onSeeked(this.video.currentTime);
        console.debug('seeked:', this.video.currentTime);

        if (!this.epicStillType) return;

        if (!this.initialSeek) {
            console.debug('Not initial Seek');
            this.stillSeekBehaviorHandler();
        } else {
            console.debug('Initial seek');
            this.initialSeek = false;
        }
        if (this.isStillType(video.epicStillTypes.APPEND)) {
            const isTheEnd = this.video.currentTime === this.video.duration;
            this.showOverlay(isTheEnd);
        }
    }

    /**
     * Behavior handler for different still types / ratings overlays during seek.
     */
    stillSeekBehaviorHandler() {
        if (this.isStillType(video.epicStillTypes.PREPEND)) {
            if (this.video.currentTime <= this.overlayTime) {
                this.showOverlay(true);
                this.handleFullPrepend();
            }
        } else if (this.isStillType(video.epicStillTypes.OVERLAY_INT)) {
            if (this.video.currentTime <= this.overlayTime) {
                this.showOverlay(true);
                const timeLeft = (this.overlayTime - this.video.currentTime) * 1000;
                this.delayRemove(timeLeft);
            }
        } else {
            console.debug(`${this.epicStillType} not handling onSeeked`);
        }
    }

    /**
     * This method toggles captions on and off the screen.
     * Secondarily it selections the correct option based on the account's Locale
     * if the caption/subtitle's exist. Other default to English.
     */
    toggleCaptions() {
        const isCaptions = this.specificMediaData.captions.length > 0;
        if (isCaptions) {
            const mode = !this.state.captionVisible ? 'showing' : 'hidden';
            const shown = this.setCaptionMode(mode, this.props.locale);
            super.toggleCaptions(shown);
        }
    }

    /**
     * Sets the captions 'mode' 'hidden' or 'showing' either on all TextTracks or a specific one.
     * If the specific label isn't available, it falls back to english. If that doesn't exist nothing is
     * set to 'showing'
     * @param mode - 'hidden' or 'showing'. Can also take 'disabled'
     * @param specific - specific label (language-LOCALE) to change the mode on. If not specified
     * it will see the mode on all TextTracks
     * @returns {boolean} - if the TextTrack(s) are visible or not
     */
    setCaptionMode(mode, specific) {
        const isEmptyObject = object => Object.keys(object).length > 0;

        const {textTracks} = this.video;

        /** Since textTracks is an 'array-like object' it doesn't have the ES6 functions.
         * So we copy them out to a 'real' array so we can use ES6 functions to make all this
         * more readable.
         */
        const tracks = [];
        for (let i = 0; i < textTracks.length; i++) tracks.push(textTracks[i]);

        if (!specific) return tracks.forEach(track => (track.mode = mode));

        const fallbackTextTrack = tracks
            .filter(({label}) => label === 'en-US')
            .reduce(
                (accumulator, current) => ({
                    ...accumulator,
                    current
                }),
                {}
            );

        tracks
            .filter(({label}) => label === specific)
            .map(track => {
                console.debug(
                    `Toggling ${this.state.captionVisible} ${this.props.locale} captions`
                );
                track.mode = mode;
                return mode === 'showing'; // It does not seem necessary to return anything here, leaving it up for you
            });

        const hasFallbackTextTrack = !isEmptyObject(fallbackTextTrack);

        if (hasFallbackTextTrack) {
            console.debug(
                `Toggling fallback ${this.state.captionVisible} ${fallbackTextTrack.label} captions`
            );
        }

        return mode === 'showing';
    }

    // RATING OVERLAY HANDLING START

    /**
     * Helper function for the PREPEND rating handling.
     */
    handleFullPrepend = () => {
        this.video.currentTime = 0;
        this.initialSeek = true;
        this.video.pause();
        this.setState({hasPlayed: false});
        this.delayedStart();
    };

    /**
     * This helper function starts playback at the `this.overlayTime`
     * seconds to allow the PREPEND slate to be shown.
     */
    delayedStart = () => {
        console.debug('delayed toggle', this.video.currentTime);
        setTimeout(() => {
            if (!browserCheck('safari')) super.toggleVideo();
            else this.setState({hasPlayed: true});

            this.showOverlay(false);
        }, this.overlayTime * 1000);
    };

    /**
     * This helper function removes the OVERLAY type rating after `this.overlayTime` seconds.
     */
    delayRemove = time => {
        this.overlayStartTime = Date.now();
        this.overlayTimeout = setTimeout(() => {
            this.showOverlay(false);
        }, time || this.overlayTime * 1000);
    };

    /**
     * General helper function for showing any type of rating overlay
     * @param show - bool to show or hide the rating overlay
     */
    showOverlay = show => {
        const state = {};
        if (this.state.overlayVisible !== show) this.setState({overlayVisible: show});

        if (this.isStillType(video.epicStillTypes.PREPEND) && this.state.disableDragging !== show) {
            state.disableDragging = show;
        }
        this.setState(state);
    };

    /**
     * This override allows for disabling of using the scrub bar to seek
     * when the PREPEND rating slate is shown.
     * @param newValue
     */
    onProgressRailUpdate = newValue => {
        console.debug('progress bar draggingDisabled:', this.state.disableDragging);
        if (!this.state.disableDragging) super.onProgressRailUpdate(newValue);
    };

    /**
     * Override to start/pause video. Different logic needs to be followed for different
     * rating overlay types.
     */
    toggleVideo() {
        if (!this.state.hasPlayed && this.epicStillType) {
            this.video.currentTime = 0;
            if (this.isStillType(video.epicStillTypes.PREPEND)) {
                this.showOverlay(true);
                this.delayedStart();
            } else if (this.isStillType(video.epicStillTypes.OVERLAY_INT)) {
                console.debug('normal toggle OVERLAY');
                super.toggleVideo();
                this.showOverlay(true);
                this.delayRemove();
            } else {
                console.debug('normal toggle');
                super.toggleVideo();
            }
        } else {
            console.debug('normal toggle');
            super.toggleVideo();
        }
    }
    // RATING OVERLAY END

    /**
     * Render the TextTracks in the Video element
     * @returns {*}
     */
    renderTracks() {
        return this.specificMediaData.captions.map(({url, lang, def, kind}) => (
            <track
                key={`${kind}_${lang}`}
                label={lang}
                srcLang={lang}
                src={url}
                kind={kind}
                default={def}
            />
        ));
    }

    /**
     * Render component override to show rating overlay when needed.
     * @returns {*} RatingOverlay component.
     */
    renderOverlay() {
        const {rating, epicStillType} = this.props;
        const {overlayVisible} = this.state;
        return (
            epicStillType && (
                <button className={styles.wrapper} onClick={this.onClick}>
                    {rating && rating.length > 0 && (
                        <RatingOverlay
                            rating={rating}
                            visible={overlayVisible}
                            epicStillType={epicStillType}
                        />
                    )}
                </button>
            )
        );
    }
}

/**
 * Check browser compatibility for the different profiles EpicPlayer supports.
 *
 * @returns {string} browser supported video profile
 */
export function browserCompatStreamSelect() {
    const tmpVideo = root.document.createElement('video');
    let res = '';
    const videoTypes = [
        {
            typeString: 'application/x-mpegURL; codecs="avc1"; audio/aac',
            internalType: video.types.HLS
        },
        {typeString: 'video/mp4; codecs="avc1"; audio/aac', internalType: video.types.DASH_FMP4},
        {typeString: 'video/webm; codecs="vp8, vorbis"', internalType: video.types.DASH_WEBM}
    ];
    if (!browserCheck('safari')) videoTypes.shift();
    const vTypeIndex = videoTypes.findIndex(t => {
        return tmpVideo.canPlayType(t.typeString) !== '';
    });
    if (vTypeIndex !== -1) res = videoTypes[vTypeIndex].internalType;
    return res;
}

/**
 * Check the data from the CMS to see if the correct data (recipe) is available.
 * Checks browser capabilities against available recipes.
 * @param recipes - array of recipe objects
 * @param locale - locale of video, falls back to 'en-US' if it doesn't exist
 * @returns {{found: boolean, recipe: null, type: null, empty: *}}
 */
export function checkEpicHosted(recipes, locale) {
    const emptyElement = <span />;
    const parsedRecipes = JSON.parse(recipes);
    let res = {found: false, empty: emptyElement, recipe: null, type: null};

    const typeFound = browserCompatStreamSelect();
    console.debug('browser supported type:', typeFound === '' ? 'none' : typeFound);

    const localeRecipes = parsedRecipes[locale] || parsedRecipes['en-US'];

    if (typeFound !== '') {
        console.debug('got EPICHOSTED type');
        const recipe = localeRecipes.find(r => {
            return r.recipe === typeFound;
        });
        if (recipe) res = {found: true, empty: null, recipe, type: typeFound};
    }
    return res;
}

/**
 * Helper function to grab the contents of a CDN file and dump it into a Blob URL
 * @param url - data to grab
 * @param type - data type for blob
 * @returns {Promise<string>} - blob: URL
 */
export async function blobifyDataFromUrl(url, type) {
    let result = '';
    await root
        .fetch(url)
        .then(res => res.text())
        .then(body => {
            result = URL.createObjectURL(new root.Blob([body], type ? {type} : {}));
        });
    return result;
}

export function browserCheck(browserString) {
    let res = false;
    switch (browserString) {
        case 'safari':
            res =
                root.navigator.userAgent.includes('Safari') &&
                !root.navigator.userAgent.includes('Chrome') &&
                !root.navigator.userAgent.includes('UnrealEngine');
            break;
        case 'launcher':
            res = root.navigator.userAgent.includes('UnrealEngine');
            break;
        default:
            res = false;
    }
    return res;
}

export default connect(
    mapMessageStateToProps,
    null,
    null,
    /**
     * This is required by Redux so we can access the ref within the connected component.
     * @see: https://react-redux.js.org/api/connect#forwardref-boolean
     */
    {forwardRef: true}
)(WithFullScreen(EpicPlayerComponent));
